// src/lib/authApi.ts - Secure API client with auth
import { getSupabase } from './supabase';

interface AuthenticatedFetch {
  get<T>(url: string): Promise<T>;
  post<T>(url: string, data?: any): Promise<T>;
  put<T>(url: string, data?: any): Promise<T>;
  delete<T>(url: string): Promise<T>;
}

class SecureApiClient implements AuthenticatedFetch {
  // Async method to get auth token using modern Supabase API
  private async getAuthTokenAsync(): Promise<string | null> {
    try {
      const supabase = getSupabase();
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('🚨 Error getting session:', error);
        return null;
      }
      
      if (session?.access_token) {
        console.log('🔑 Found auth token via async getSession()');
        return session.access_token;
      }
      
      console.warn('⚠️ No session found via async getSession()');
      return null;
    } catch (error) {
      console.error('❌ Error in getAuthTokenAsync:', error);
      return null;
    }
  }

  private getAuthToken(): string | null {
    try {
      // Method 1: Try to get token directly from initialized Supabase client
      try {
        const supabase = getSupabase();
        // For newer Supabase versions, we need to get the session synchronously
        // The session is cached in the client after auth
        const session = (supabase.auth as any).session || (supabase.auth as any)._session;
        
        if (session?.access_token) {
          console.log('🔑 Found auth token via Supabase client session');
          return session.access_token;
        }
      } catch (supabaseError) {
        console.log('⚠️ Supabase client not initialized or no session, trying localStorage...');
      }
      
      // Method 2: Scan localStorage for Supabase session keys
      console.log('🔍 Scanning localStorage for Supabase session...');
      const allKeys = Object.keys(localStorage);
      const supabaseKeys = allKeys.filter(key => 
        (key.startsWith('sb-') && key.includes('auth-token')) ||
        key.includes('supabase') && key.includes('auth')
      );
      
      console.log('🔍 Found potential Supabase keys:', supabaseKeys);
      
      for (const key of supabaseKeys) {
        try {
          const sessionData = localStorage.getItem(key);
          if (sessionData) {
            const session = JSON.parse(sessionData);
            
            // Try different possible structures based on Supabase versions
            let token = null;
            if (session.access_token) {
              token = session.access_token;
            } else if (session.session?.access_token) {
              token = session.session.access_token;
            } else if (session.currentSession?.access_token) {
              token = session.currentSession.access_token;
            } else if (session.data?.session?.access_token) {
              token = session.data.session.access_token;
            }
            
            if (token) {
              console.log('🔑 Found auth token via localStorage key:', key);
              return token;
            } else {
              console.log('🔍 Session data structure for key', key, ':', Object.keys(session));
            }
          }
        } catch (parseError) {
          console.warn('⚠️ Failed to parse session data for key:', key);
          continue;
        }
      }
      
      console.error('❌ No valid auth token found in any method');
      console.log('🔍 All localStorage keys containing "sb" or "supabase":', 
        allKeys.filter(k => k.includes('sb') || k.includes('supabase')));
      
    } catch (error) {
      console.error('❌ Error getting auth token:', error);
    }
    return null;
  }

  private async makeRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    // Try async method first, then fallback to sync method
    let token = await this.getAuthTokenAsync();
    if (!token) {
      console.log('🔄 Async token failed, trying sync method...');
      token = this.getAuthToken();
    }
    
    // Debug logging for authentication
    console.log('🔐 Making API request to:', url);
    console.log('🔑 Auth token available:', !!token);
    if (token) {
      console.log('🔑 Token prefix:', token.substring(0, 20) + '...');
    }
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth token if available
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      console.log('✅ Added Authorization header');
    } else {
      console.warn('⚠️ No auth token available - request will fail if auth required');
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Log full error details for debugging but don't expose to users
      console.error('API request failed:', {
        url,
        status: response.status,
        statusText: response.statusText,
        serverMessage: errorData.message,
        serverError: errorData.error,
        serverCode: errorData.code,
        serverDetails: errorData.details,
        serverHint: errorData.hint,
        fullResponse: errorData
      });
      throw new Error('Request failed');
    }

    return response.json();
  }

  async get<T>(url: string): Promise<T> {
    return this.makeRequest<T>(url, { method: 'GET' });
  }

  async post<T>(url: string, data?: any): Promise<T> {
    return this.makeRequest<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(url: string, data?: any): Promise<T> {
    return this.makeRequest<T>(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(url: string): Promise<T> {
    const token = this.getAuthToken();
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add auth token if available
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Log full error details for debugging but don't expose to users
      console.error('API request failed:', {
        url,
        status: response.status,
        statusText: response.statusText,
        serverMessage: errorData.message
      });
      throw new Error('Request failed');
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }
}

// Export singleton instance
export const secureApi = new SecureApiClient();

// Template API functions
export const templateApi = {
  getAll: () => secureApi.get<any[]>('/api/templates'),
  getById: (id: string) => secureApi.get<any>(`/api/templates?templateId=${id}`),
  create: (template: any) => secureApi.post<any>('/api/templates', template),
  update: (id: string, updates: any) => secureApi.put<any>(`/api/templates?templateId=${id}`, updates),
  delete: (id: string) => secureApi.delete<void>(`/api/templates?templateId=${id}`),
};

// Auth API functions
export const authApi = {
  validateSession: async (): Promise<{valid: boolean; user?: any}> => {
    try {
      return await secureApi.get<{valid: boolean; user?: any}>('/api/auth-session');
    } catch (error) {
      // If there's no token or server error, return invalid session
      console.log('Session validation failed:', error);
      return { valid: false };
    }
  },
  refreshSession: (refreshToken: string) => secureApi.post<any>('/api/auth-session', { refresh_token: refreshToken }),
};

// Project API functions (using existing endpoint)
export const projectApi = {
  getAll: async () => {
    const response = await secureApi.get<{success: boolean, data: any[]}>('/api/projects');
    return response.success ? response.data : [];
  },
  getById: async (id: string) => {
    const response = await secureApi.get<{success: boolean, data: any}>(`/api/projects?projectId=${id}`);
    
    // DEBUG: Log what we received from the API
    if (response.success && response.data) {
      console.log('🔍 [Client API] Received project from server:', {
        projectId: response.data.id,
        period_type: response.data.period_type,
        typeof_period_type: typeof response.data.period_type
      });
    }
    
    return response.success ? response.data : null;
  },
  create: async (project: any) => {
    const response = await secureApi.post<{success: boolean, data: any}>('/api/projects', project);
    return response.success ? response.data : null;
  },
  update: async (id: string, updates: any) => {
    const response = await secureApi.put<{success: boolean, data: any}>(`/api/projects?projectId=${id}`, updates);
    return response.success ? response.data : null;
  },
  delete: (id: string) => secureApi.delete<any>(`/api/projects?projectId=${id}`),
};

// Financial Reports API functions
export const reportsApi = {
  getAllByProject: async (projectId: string) => {
    const response = await secureApi.get<{success: boolean, data: any[]}>(`/api/financial-reports?projectId=${projectId}`);
    return response.success ? response.data : [];
  },
  getByProjectAndType: async (projectId: string, reportType: string) => {
    const response = await secureApi.get<{success: boolean, data: any}>(`/api/financial-reports?projectId=${projectId}&reportType=${reportType}`);
    return response.success ? response.data : null;
  },
  upsert: async (projectId: string, reportType: string, data: any, templateId?: string) => {
    const response = await secureApi.post<{success: boolean, data: any}>('/api/financial-reports', {
      project_id: projectId,
      report_type: reportType,
      data,
      template_id: templateId
    });
    return response.success ? response.data : null;
  },
  delete: async (projectId: string, reportType: string) => {
    const response = await secureApi.delete<{success: boolean}>(`/api/financial-reports?projectId=${projectId}&reportType=${reportType}`);
    return response.success;
  },
};

// Export Settings API functions
export const exportSettingsApi = {
  get: async (userId: string, projectId?: string) => {
    const url = projectId 
      ? `/api/export-settings?userId=${userId}&projectId=${projectId}`
      : `/api/export-settings?userId=${userId}`;
    const response = await secureApi.get<{success: boolean, data: any}>(url);
    return response.success ? response.data : null;
  },
  save: async (userId: string, settings: any, projectId?: string) => {
    const response = await secureApi.post<{success: boolean, data: any}>('/api/export-settings', {
      user_id: userId,
      project_id: projectId,
      settings
    });
    return response.success ? response.data : null;
  },
  delete: async (userId: string, projectId?: string) => {
    const url = projectId 
      ? `/api/export-settings?userId=${userId}&projectId=${projectId}`
      : `/api/export-settings?userId=${userId}`;
    const response = await secureApi.delete<{success: boolean}>(url);
    return response.success;
  },
};